import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Gift, X, MessageSquare } from 'lucide-react';
import { Button } from './ui/button';
import { MessageWithSender } from '../types/message';

interface SecretSantaMessagePopupProps {
  message: MessageWithSender & { group_name?: string };
  onDismiss: () => void;
  onView: () => void;
}

const SecretSantaMessagePopup: React.FC<SecretSantaMessagePopupProps> = ({
  message,
  onDismiss,
  onView,
}) => {
  const [isVisible, setIsVisible] = useState(true);

  const handleDismiss = () => {
    setIsVisible(false);
    setTimeout(() => onDismiss(), 300);
  };

  const handleView = () => {
    setIsVisible(false);
    setTimeout(() => onView(), 300);
  };

  const preview = message.content.length > 100 
    ? message.content.substring(0, 100) + '...' 
    : message.content;

  if (!isVisible) {
    return null;
  }

  return (
    <>
      {/* Backdrop */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.3 }}
        className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm"
        onClick={handleDismiss}
      />
      
      {/* Popup */}
      <motion.div
        initial={{ opacity: 0, scale: 0.9, y: 20 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.9, y: 20 }}
        transition={{ 
          type: "spring",
          stiffness: 300,
          damping: 30
        }}
        className="fixed left-1/2 top-1/2 z-50 w-full max-w-md -translate-x-1/2 -translate-y-1/2"
        onClick={(e) => e.stopPropagation()}
      >
            <div className="mx-4 rounded-lg border border-border/50 bg-card shadow-lg">
              {/* Header */}
              <div className="flex items-center justify-between border-b border-border/50 bg-purple-100/20 dark:bg-purple-900/20 p-4">
                <div className="flex items-center gap-3">
                  <div className="flex h-10 w-10 items-center justify-center rounded-full bg-purple-500/20">
                    <Gift className="h-5 w-5 text-purple-600 dark:text-purple-400" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-foreground">Secret Santa Message</h3>
                    {message.group_name && (
                      <p className="text-sm text-muted-foreground">{message.group_name}</p>
                    )}
                  </div>
                </div>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={handleDismiss}
                  className="h-8 w-8"
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>

              {/* Content */}
              <div className="p-4">
                <p className="text-sm text-muted-foreground mb-3">
                  You have received a new Secret Santa message!
                </p>
                <div className="rounded-md bg-muted/50 p-3 border border-border/30">
                  <p className="text-sm text-foreground whitespace-pre-wrap wrap-break-word">
                    {preview}
                  </p>
                </div>
              </div>

              {/* Actions */}
              <div className="flex gap-2 border-t border-border/50 p-4">
                <Button
                  variant="outline"
                  onClick={handleDismiss}
                  className="flex-1"
                >
                  Dismiss
                </Button>
                <Button
                  onClick={handleView}
                  className="flex-1 bg-purple-600 hover:bg-purple-700 text-white"
                >
                  <MessageSquare className="h-4 w-4 mr-2" />
                  View Message
                </Button>
              </div>
            </div>
          </motion.div>
    </>
  );
};

export default SecretSantaMessagePopup;

